import Box from '@mui/material/Box'
import Image, { StaticImageData } from 'next/image'
import { ReactNode } from 'react'

import TitleAndParagraph from '@/components/TitleAndParagraph'
import TwoColumnLayout, { ColumnWrapOrder } from '@/components/TwoColumnLayout'

/**
 * TwoColumnLayout with Text and Image, but allows for a custom image ratio
 * (unlike TwoColumnLayoutWithBackgroundImage which will distort non-rectangular images.)
 * @param image - the image to display
 * @param imageAlt - the alt text for the image
 * @param imagePaddingBottom - the padding-bottom value for the image, as a string with % appendix, e.g. '108.64%'. A bit of a hack to maintain aspect ratio.
 * This will be image-specific and should be calculated correctly. The image will be stretched if wrong value is provided.
 * @param title - the title to display alongside the image
 * @param description - the description to display alongside the image
 */
const TwoColumnLayoutWithCustomImageRatio = ({
    image,
    imageAlt,
    imagePaddingBottom,
    title,
    description,
    columnWrapOrderOnMobile,
    snippet,
}: {
    image: StaticImageData
    imageAlt: string
    imagePaddingBottom: string
    title: string
    description: ReactNode
    columnWrapOrderOnMobile: ColumnWrapOrder
    snippet?: ReactNode
}) => (
    <Box>
        <TwoColumnLayout leftCol={6} rightCol={5} columnWrapOrderOnMobile={columnWrapOrderOnMobile}>
            <TitleAndParagraph
                sx={{
                    maxWidth: {
                        xs: '100%',
                        sm: '100%',
                        md: '588px',
                        lg: '588px',
                        xl: '588px',
                    },
                }}
                title={title}
                paragraph={description}
                snippet={snippet}
            />
            <Box sx={{ display: `flex` }}>
                <Box
                    sx={{
                        width: '100%',
                        height: '0',
                        paddingBottom: imagePaddingBottom,
                        position: 'relative',
                        img: {
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: 0,
                            bottom: 0,
                            width: '100%',
                            height: '100%',
                        },
                    }}
                >
                    <Image src={image} alt={imageAlt} />
                </Box>
            </Box>
        </TwoColumnLayout>
    </Box>
)

export default TwoColumnLayoutWithCustomImageRatio
